import { Loader } from "@/components/loader";
import { Box, Button, Grid, Typography } from "@mui/material";
import moment from "jalali-moment";
import { useCallback, useEffect, useState } from "react";
import { getActiveAreaId, getTrainList } from "../../../../api/area";
import SearchInput from "../../../project/searchInput";
import TableTrainList from "../../table/tableTrainList";

export default function ChildrenEducation() {
  const [trainedPatients, setTrainedPatients] = useState([]);
  const [notTrainedPatients, setNotTrainedPatients] = useState([]);

  const [valueSearch, setValueSearch] = useState("");
  const [valueSearchBeforeTrain, setValueSearchBeforeTrain] = useState("");

  const [trainPageable, setTrainPageable] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [notTrainPageable, setNotTrainPageable] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [trainTotalSize, setTrainTotalSize] = useState();
  const [notTrainTotalSize, setNotTrainTotalSize] = useState();

  const [trainLoading, setTrainLoading] = useState(false);
  const [notTrainLoading, setNotTrainLoading] = useState(false);

  const [notTrainLastFetch, setNotTrainLastFetch] = useState();
  const [trainLastFetch, setTrainLastFetch] = useState();

  const [areaId, setAreaId] = useState();

  useEffect(() => {
    getActiveAreaId().then((res) => {
      setAreaId(res);
    });
  }, []);

  const fetchNotTrainedPatients = useCallback(
    (refetchTotalElements, clearSearch) => {
      if (
        notTrainLastFetch !== undefined &&
        Date.now() - notTrainLastFetch < 500
      )
        return;

      setNotTrainLoading(true);
      const query = new URLSearchParams();
      query.append("justNotTrained", true);
      query.append("justChildren", true);
      query.append("pageIndex", notTrainPageable.pageIndex);
      query.append("pageSize", notTrainPageable.pageSize);
      if (
        !clearSearch &&
        valueSearchBeforeTrain &&
        valueSearchBeforeTrain.length > 1
      ) {
        query.append("search", valueSearchBeforeTrain);
      }
      query.append(
        "needTotalElements",
        notTrainTotalSize === undefined || refetchTotalElements,
      );

      getTrainList(areaId, `?${query.toString()}`)
        .then((res) => {
          if (res) {
            setNotTrainLastFetch(Date.now());
            setNotTrainedPatients(res.patients);

            if (refetchTotalElements) {
              setNotTrainPageable({
                pageIndex: 0,
                pageSize: 10,
              });
            }
            if (notTrainTotalSize === undefined || refetchTotalElements)
              setNotTrainTotalSize(res.totalElements);
          }
        })
        .finally(() => setNotTrainLoading(false));
    },
    [areaId, notTrainPageable, valueSearchBeforeTrain],
  );

  useEffect(() => {
    if (areaId) {
      fetchNotTrainedPatients(false, false);
    }
  }, [areaId, notTrainPageable]);

  const fetchTrainedPatients = useCallback(
    (refetchTotalElements, clearSearch) => {
      if (trainLastFetch !== undefined && Date.now() - trainLastFetch < 500)
        return;

      setTrainLoading(true);
      const query = new URLSearchParams();
      query.append("justTrained", true);
      query.append("justChildren", true);
      query.append("pageIndex", trainPageable.pageIndex);
      query.append("pageSize", trainPageable.pageSize);
      if (!clearSearch && valueSearch && valueSearch.length > 1) {
        query.append("search", valueSearch);
      }
      query.append(
        "needTotalElements",
        trainTotalSize === undefined || refetchTotalElements,
      );

      getTrainList(areaId, `?${query.toString()}`)
        .then((res) => {
          if (res) {
            setTrainLastFetch(Date.now());
            setTrainedPatients(res.patients);

            if (refetchTotalElements) {
              setTrainPageable({
                pageIndex: 0,
                pageSize: 10,
              });
            }
            if (trainTotalSize === undefined || refetchTotalElements)
              setTrainTotalSize(res.totalElements);
          }
        })
        .finally(() => setTrainLoading(false));
    },
    [areaId, trainPageable, valueSearch],
  );

  useEffect(() => {
    if (areaId) {
      fetchTrainedPatients(false, false);
    }
  }, [areaId, trainPageable]);

  const handlePatientClick = useCallback(
    (oldTrainStatus, data) => {
      const now = moment();
      const jalaliNow = now.locale("fa").format("YYYY/MM/DD HH:mm");
      data.createdAt = jalaliNow;
      data.trained = !oldTrainStatus;

      if (oldTrainStatus) {
        setNotTrainedPatients([...notTrainedPatients, data]);
        setTrainedPatients(trainedPatients.filter((row) => row.id !== data.id));
        setTrainTotalSize(trainTotalSize - 1);
        setNotTrainTotalSize(notTrainTotalSize + 1);
      } else {
        setNotTrainedPatients(
          notTrainedPatients.filter((row) => row.id !== data.id),
        );
        setTrainedPatients([data, ...trainedPatients]);
        setTrainTotalSize(trainTotalSize + 1);
        setNotTrainTotalSize(notTrainTotalSize - 1);
      }
    },
    [trainedPatients, trainTotalSize, notTrainedPatients, notTrainTotalSize],
  );

  const [notTrainedTimer, setNotTrainedTimer] = useState();

  useEffect(() => {
    if (notTrainedTimer !== undefined) {
      clearTimeout(notTrainedTimer);
    }

    if (valueSearchBeforeTrain.length >= 2) {
      const timer = setTimeout(() => {
        fetchNotTrainedPatients(true, false);
      }, 500);

      setNotTrainedTimer(timer);
    }

    return () => {
      if (notTrainedTimer !== undefined) {
        clearTimeout(notTrainedTimer);
      }
    };
  }, [valueSearchBeforeTrain]);

  const [trainedTimer, setTrainedTimer] = useState();

  useEffect(() => {
    if (trainedTimer !== undefined) {
      clearTimeout(trainedTimer);
    }

    if (valueSearch.length >= 2) {
      const timer = setTimeout(() => {
        fetchTrainedPatients(true, false);
      }, 500);

      setTrainedTimer(timer);
    }

    return () => {
      if (trainedTimer !== undefined) {
        clearTimeout(trainedTimer);
      }
    };
  }, [valueSearch]);

  return (
    <Box>
      <>
        {trainLoading || notTrainLoading ? (
          <Loader />
        ) : (
          <>
            <Typography
              my="20px"
              color="var(--emr-primary)"
              fontSize="18px"
              fontWeight="bold"
            >
              جدول بیماران کودکان پذیرش نشده
            </Typography>
            <Grid display={"flex"} flexDirection={"row"}>
              <SearchInput
                type="text"
                id="filter-text-box-before-train"
                value={valueSearchBeforeTrain}
                onChange={(e) => setValueSearchBeforeTrain(e.target.value)}
                mb="5px"
              />
              {valueSearchBeforeTrain.length >= 2 && (
                <Button
                  onClick={() => {
                    setValueSearchBeforeTrain("");
                    fetchNotTrainedPatients(true, true);
                  }}
                >
                  پاک کردن فیلتر
                </Button>
              )}
            </Grid>
            {areaId && (
              <TableTrainList
                handlePatientClick={handlePatientClick}
                areaId={areaId}
                children
                rowData={notTrainedPatients}
                pageable={notTrainPageable}
                totalSize={notTrainTotalSize}
                setPageable={setNotTrainPageable}
              />
            )}
            <Typography
              my="20px"
              mt="50px"
              color="var(--emr-primary)"
              fontSize="18px"
              fontWeight="bold"
            >
              جدول بیماران کودکان پذیرش شده
            </Typography>
            <Grid display={"flex"} flexDirection={"row"}>
              <SearchInput
                type="text"
                id="filter-text-box-before-train"
                value={valueSearch}
                onChange={(e) => setValueSearch(e.target.value)}
                mb="5px"
              />
              {valueSearch.length >= 2 && (
                <Button
                  onClick={() => {
                    setValueSearch("");
                    fetchTrainedPatients(true, true);
                  }}
                >
                  پاک کردن فیلتر
                </Button>
              )}
            </Grid>
            {areaId && (
              <TableTrainList
                handlePatientClick={handlePatientClick}
                areaId={areaId}
                children
                rowData={trainedPatients}
                pageable={trainPageable}
                totalSize={trainTotalSize}
                setPageable={setTrainPageable}
              />
            )}
          </>
        )}
      </>
    </Box>
  );
}
